import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import { COUNTRIES } from "@/lib/api";
import { inputCls } from "@/components/Ui";

export const CountrySelect = ({ value, onChange, testId = "country-select", required }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const box = useRef(null);

  useEffect(() => {
    const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? COUNTRIES.filter((c) => c.toLowerCase().includes(t)) : COUNTRIES;
  }, [q]);

  return (
    <div className="relative" ref={box}>
      <button type="button" data-testid={testId} onClick={() => { setOpen((o) => !o); setQ(""); }}
        aria-haspopup="listbox" aria-expanded={open}
        onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); if (e.key === "ArrowDown") { setOpen(true); setQ(""); } }}
        className={`${inputCls} flex items-center justify-between text-left ${
          required && !value ? "border-[#B42318]" : ""}`}>
        <span className={value ? "text-[#18232F]" : "text-[#8598ac]"}>{value || "Select country"}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-[#667788]" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-[10px] border border-[#DFE6ED] bg-white shadow-xl"
          data-testid={`${testId}-menu`} role="listbox">
          <div className="flex items-center gap-2 border-b border-[#DFE6ED] px-3 py-2">
            <Search className="h-3.5 w-3.5 text-[#667788]" />
            <input autoFocus data-testid={`${testId}-search`} value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setOpen(false);
                if (e.key === "Enter" && list.length) { e.preventDefault(); onChange(list[0]); setOpen(false); }
              }}
              placeholder="Search country" className="w-full text-sm outline-none" />
          </div>
          <ul className="max-h-60 overflow-y-auto py-1">
            {list.length === 0 && <li className="px-3 py-2 text-sm text-[#8598ac]">No match</li>}
            {list.map((c) => (
              <li key={c} role="option" aria-selected={c === value}>
                <button type="button" data-testid={`${testId}-option-${c}`}
                  onClick={() => { onChange(c); setOpen(false); }}
                  className={`w-full px-3 py-2 text-left text-sm transition-colors hover:bg-[#EAF4F3] ${
                    c === value ? "font-bold text-[#147D78]" : "text-[#334e68]"}`}>
                  {c}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
